import React, { ReactElement, useEffect, useState } from 'react'
import { PrimaryButton } from './Button'
import Radom, { ICheckoutSession, PaymentSession, DonationLinkOrder } from '../state/Radom'
import { RadioButtonGroup } from './RadioButton'
import { InputLabel, TextInput } from './Input'
import NumberInput from './NumberInput'
import Switch from './Switch'
import Tooltip from './Tooltip'
import { RADOM_COLORS } from '../util/Constants'
import PaymentMethodDisplay from './PaymentMethodDisplay'
import { errorToast, successToast, trimZeros } from '../util/Util'
import { getMethod } from '../util/Managed'

interface RefundProps {
  checkoutSession?: ICheckoutSession
  paymentSession?: PaymentSession
  donationLinkOrder?: DonationLinkOrder
  onRefund: () => void
}

const RefundForm = (props: RefundProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const [isFullRefund, setIsFullRefund] = useState(true)
  const [chargeCustomerNetworkFee, setChargeCustomerNetworkFee] = useState(true)

  const payment = props.checkoutSession?.payment?.managed ||
    props.checkoutSession?.payment?.managedTokenDelegatePayment ||
    props.checkoutSession?.payment?.openBanking ||
    props.paymentSession?.payment?.managed ||
    props.donationLinkOrder?.payment?.managed

  const currency = props.checkoutSession?.payment?.openBanking ? props.checkoutSession?.currency : getMethod(props.checkoutSession?.payment?.managed?.network || '', props.checkoutSession?.payment?.managed?.token).ticker

  if (!payment) {
    return <></>
  }

  const [refundAmount, setRefundAmount] = useState(payment.amount)
  const [recipientAddress, setRecipientAddress] = useState('')

  const submitRefund = async (): Promise<void> => {
    setIsLoading(true)

    Radom.refund({
      paymentSessionId: props.paymentSession?.id,
      checkoutSessionId: props.checkoutSession?.id,
      donationLinkOrderId: props.donationLinkOrder?.id,
      refundAmount,
      recipientAddress,
      chargeCustomerNetworkFee
    }).then(() => {
      successToast('Refund submitted')
      props.onRefund()
    }).catch(err => {
      const isPermissionError = err?.response?.status === 401 || err?.response?.status === 403
      errorToast(
        isPermissionError
          ? 'Write refunds permission is required to issue refunds'
          : err.reason || err.response?.data?.error || err.message
      )
    }).finally(() => {
      setIsLoading(false)
    })
  }

  useEffect(() => {
    if (isFullRefund) setRefundAmount(payment.amount)
  }, [isFullRefund])

  return <div style={{
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }}>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <InputLabel>Refund type</InputLabel>
      <RadioButtonGroup
        disabled={isLoading}
        labels={['Full refund', 'Partial refund']}
        selectedLabel={isFullRefund ? 'Full refund' : 'Partial refund'}
        onSelect={label => setIsFullRefund(label === 'Full refund')}
      />
    </div>

    {
      !isFullRefund &&
      <div style={{ position: 'relative' }}>
        <PaymentMethodDisplay
          style={{
            position: 'absolute',
            left: 5,
            top: 4,
            zIndex: 1,
            transform: 'scale(0.85)',
            pointerEvents: 'none'
          }}
          method={{ network: payment.network, token: payment.token }}
          excludeName />
        <NumberInput
          style={{ textIndent: 20 }}
          disabled={isLoading}
          autoFocus
          placeholder="Refund amount"
          min="0"
          autoComplete="off"
          value={refundAmount}
          fontSize="14"
          onUserInput={(e) => setRefundAmount(e)}
        />
      </div>
    }

    {
      !props.checkoutSession?.payment?.openBanking && <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 3 }}>
            <InputLabel>Charge customer network fee</InputLabel>
            <Tooltip message='If this is enabled, the gas fee to transfer the refund will be deducted from the refund amount.' />
          </div>
          <Switch
            checked={chargeCustomerNetworkFee}
            onClick={() => setChargeCustomerNetworkFee(!chargeCustomerNetworkFee)}
            disabled={isLoading} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InputLabel>Recipient address</InputLabel>
          <TextInput
            disabled={isLoading}
            autoFocus
            placeholder=''
            value={recipientAddress}
            onChange={e => setRecipientAddress(e.target.value)} />
        </div>
      </>
    }

    {
      false &&
      props.checkoutSession?.payment?.openBanking &&
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <InputLabel>Radom refund fee (3% + 10EUR)</InputLabel>
        <TextInput
          disabled={true}
          autoFocus
          placeholder=''
          value={(parseFloat(refundAmount || '0') * 0.03 + 10).toFixed(2)}
          onChange={e => setRecipientAddress(e.target.value)} />
      </div>
    }

    <div style={{ width: '100%', height: 1, backgroundColor: RADOM_COLORS.GRAY_MED, margin: '10px 0' }} />

    <div style={{
      display: 'flex',
      color: RADOM_COLORS.GRAY_DARKEST,
      flexDirection: 'column'
    }}>
      <div style={{
        alignItems: 'center',
        display: 'flex',
        border: `1px solid ${RADOM_COLORS.ORANGE_LIGHT}`,
        borderRadius: 10,
        gap: 15,
        borderBottom: `1px solid ${RADOM_COLORS.ORANGE_LIGHT}`,
        backgroundColor: RADOM_COLORS.ORANGE_LIGHTEST,
        padding: 20,
        borderBottomLeftRadius: props.checkoutSession?.payment?.openBanking ? 0 : 10,
        borderBottomRightRadius: props.checkoutSession?.payment?.openBanking ? 0 : 10

      }}>
        <PaymentMethodDisplay method={{ network: payment.network, token: payment.token }} excludeName />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span>
            <span
              style={{
                fontWeight: 600
              }}
            >
              {props.checkoutSession?.payment?.openBanking
                ? (parseFloat(refundAmount || '0') * 0.03 + 10 + parseFloat(refundAmount || '0')).toFixed(2)
                : trimZeros(refundAmount)}
            </span>{' '}
            <span style={{ fontWeight: 600 }}>
              {currency} {!chargeCustomerNetworkFee && '+ gas fee'}
            </span>{' '}
            {props.checkoutSession?.payment?.openBanking
              ? (
                <>will be converted to <span style={{ fontWeight: 600 }}>USDC</span> and </>
              )
              : (
                'will be '
              )}
              withdrawn from your balance
          </span>
        </div>
      </div>
      {
        props.checkoutSession?.payment?.openBanking &&
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          fontSize: 14,
          padding: 20,
          border: `1px solid ${RADOM_COLORS.GRAY9}`,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderTop: 0
        }}>
          <div style={{ display: 'flex', gap: 3, justifyContent: 'space-between' }}>

            <span style={{}}>Open banking withdrawal fee</span>
            <span style={{ fontWeight: 600 }}>{10} {props.checkoutSession?.currency}  + 3%</span>
          </div>

          <div style={{ display: 'flex', gap: 3, justifyContent: 'space-between' }}>

            <span style={{}}>Refunded to customer</span>
            <span style={{ fontWeight: 600 }}>{refundAmount} {props.checkoutSession?.currency}</span>
          </div>
        </div>
      }
    </div>

    <PrimaryButton
      style={{ width: '100%' }}
      onClick={submitRefund}
      isLoading={isLoading}>
      Issue refund
    </PrimaryButton>
  </div>
}

export default RefundForm
